<script setup lang="ts">
import ScaleMenuState from "@/stores/ScaleMenuState";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Facade } from "@/drawing";
import type { ScaleConfig } from "@/types/legend.types";
import { Logger } from "@/logger";
import ScaleItem from "../DrawerMenus/Settings/ScaleItem.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const props = defineProps<{
  target: string;
  /** The selection is a dimension: the size goes through its own API. */
  dimension?: boolean;
}>();

const input = ref<number>();

// The Facade is not reactive, so the displayed height must be re-read by
// hand: when the selected entity changes (target prop) and after every
// change applied from this dropdown. A computed would be evaluated once and
// stay stale forever.
const size = ref<number | "-">("-");

function refreshSize() {
  if (!Facade.isInitialized) {
    size.value = "-";
    return;
  }
  const height = props.dimension
    ? Facade.dimensions.getSelectedTextHeight()
    : Facade.text.getSelectedTextHeight();
  size.value = height ?? "-";
}

watch(() => props.target, refreshSize, { immediate: true });

// Undoing a change of height leaves the selection as it is.
onMounted(() => {
  if (Facade.isInitialized) Facade.history.onChange(refreshSize);
});

onBeforeUnmount(() => {
  if (Facade.isInitialized) Facade.history.offChange(refreshSize);
});

function insertAndSetProps(config: ScaleConfig) {
  Logger.info(`ScaleDropdown : insert and set props`);
  if (!Facade.isInitialized) return;

  if (props.dimension) {
    Facade.dimensions.setSelectedScale(config);
    refreshSize();
    return;
  }

  // 1. Apply textstyle if present (includes style.size as height)
  if (config.props.textstyle) {
    Facade.text.setSelectedTextStyle(config.props.textstyle);
  }

  // 2. Apply explicit textsize on top (overrides style.size)
  if (config.props.textsize) {
    Facade.text.setSelectedTextHeight(config.props.textsize);
  }

  refreshSize();
}

function setHeight() {
  Logger.info(`ScaleDropdown : set height to ${input.value}`);
  if (input.value && Facade.isInitialized) {
    if (props.dimension) {
      Facade.dimensions.setSelectedTextHeight(input.value);
    } else {
      Facade.text.setSelectedTextHeight(input.value);
    }
    refreshSize();
  }
}
</script>
<template>
  <button
    type="button"
    class="canvas-btn canvas-btn-nosize dropdown-toggle scale-btn"
    data-bs-toggle="dropdown"
    aria-expanded="false"
  >
    h({{ size }})
  </button>
  <ul
    class="dropdown-menu canvas-dropdown overflow-y-auto"
    style="max-height: 45vh"
  >
    <li>
      <div class="input-group p-1">
        <input
          type="number"
          inputmode="decimal"
          enterkeyhint="done"
          class="form-control"
          v-model="input"
          style="width: 50px"
          @keydown.enter.prevent="
            setHeight();
            ($event.target as HTMLInputElement).blur();
          "
        />
        <span
          type="button"
          class="input-group-text"
          aria-expanded="false"
          @click="setHeight()"
        >
          <PhosphorIcon name="check" :size="14" />
        </span>
      </div>
    </li>
    <li>
      <h6 class="dropdown-header">
        {{ $t("canvas:floating.scaledropdown.header.custom") }}
      </h6>
    </li>
    <li
      v-for="scale in ScaleMenuState.scales"
      :key="scale.name"
      @click="() => insertAndSetProps(scale)"
    >
      <ScaleItem :scale="scale" />
    </li>
  </ul>
</template>

<!-- Le style sombre du menu (.canvas-dropdown) est global, dans app.scss -->
<style lang="scss" scoped>
// Toute la hauteur de la barre, comme les autres boutons.
.scale-btn {
  height: 36px;
  padding: 0 10px;
  gap: 4px;
}
</style>
